// Identity context that rides on every log record so frontend events can be
// correlated with backend traces.
//
//   sessionId  — generated once at app boot, never cleared. Stable across the
//                whole browser session (until tab close / hard reload).
//   userId     — set on login success, cleared on logout. Null while
//                unauthenticated.
//
// Use `core/monitoring.identifyUser()` / `clearUser()` from the auth flow
// rather than calling `setUserId` here directly — those fan-out helpers also
// notify Sentry + Mixpanel so the three sinks stay in sync.

// Base-36 (0-9 + a-z) keeps the fallback session id compact while staying
// URL-safe — only used when `crypto.randomUUID` is unavailable.
const BASE_36 = 36

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Fallback for environments without WebCrypto (older test runners).
  return `s-${Math.random().toString(BASE_36).slice(2)}-${Date.now().toString(BASE_36)}`
}

const sessionId = generateId()
let userId: string | null = null

export const getSessionId = (): string => sessionId
export const getUserId = (): string | null => userId

// Internal — call via `core/monitoring.identifyUser()` so Sentry + analytics
// pick up the same id. Direct calls are fine inside tests.
export const setUserId = (id: string | null): void => {
  userId = id
}
